import './CustomerInsightsChart.scss';
import cn from 'classnames';
import {
  Dispatch,
  ReactElement,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Cell, Legend, Pie, PieChart } from 'recharts';
import { formatNumber, formatPercent } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { KnowYourCustomerData } from '@blisspointmedia/bpm-types/dist/KnowYourCustomer';
import { ChartContainer } from '@Components/ChartContainer';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { useContainerSize } from '../../utils/hooks/useContainerSize';
import { ChartContainerRightActions } from './ChartContainerRightActions';
import {
  CHART_TYPE_MAP,
  ChartDataPoint,
  ChartType,
  CUSTOMER_INSIGHTS_PRETTY_NAMES,
  CustomerInsightsFields
} from './customer-insights-constants';
import { buildColorMap } from './customer-insights-utils';
import { CustomerInsightsLegend } from './CustomerInsightsLegend';

const PENETRATION_BAR_HEIGHT = 48;

interface StackedBarProps {
  data: KnowYourCustomerData[];
  fieldPrettyName: string;
  setSelectedValue: Dispatch<SetStateAction<string | undefined>>;
  chartContainerRightActions: ReactElement;
  decimalPrecision: number;
}

const StackedBar = ({
  data,
  fieldPrettyName,
  setSelectedValue,
  chartContainerRightActions,
  decimalPrecision
}: StackedBarProps) => {
  const [hoveredValue, setHoveredValue] = useState<string | undefined>();
  const barMax = Math.max(
    ...data.map(val => val.clientPopulation),
    ...data.map(val => val.tuPopulation)
  );
  const colorMap = useMemo(() => buildColorMap(data), [data]);

  const legendFormattedData = useMemo(
    () =>
      data.map(({ levels, clientPopulation, tuPopulation }) => ({
        name: levels,
        val1: formatNumber(Math.round(clientPopulation)),
        val2: formatNumber(Math.round(tuPopulation)),
        computedValue: formatPercent(clientPopulation / tuPopulation, decimalPrecision)
      })),
    [data, decimalPrecision]
  );

  return (
    <div className='customer-insights-bar-chart penetration-bar-chart'>
      <ChartContainer title={fieldPrettyName} rightActions={chartContainerRightActions}>
        {data.map(({ levels, clientPopulation, tuPopulation }, index) => {
          const customerVal = Math.round(clientPopulation);
          const remainderVal = Math.round(tuPopulation - clientPopulation);
          return (
            <InteractiveDiv
              key={levels}
              className={cn('penetration-bar-container', {
                'hovered-value': levels === hoveredValue,
                'unhovered-value': Boolean(hoveredValue) && levels !== hoveredValue
              })}
              style={{ height: PENETRATION_BAR_HEIGHT }}
              onClick={() => setSelectedValue(levels)}
              onMouseEnter={() => setHoveredValue(levels)}
              onMouseLeave={() => setHoveredValue(undefined)}
            >
              <CustomerInsightsLegend
                data={[legendFormattedData[index]]}
                hoveredValue={hoveredValue}
                setHoveredValue={setHoveredValue}
              />
              <div className='penetration-bars'>
                <div
                  className='penetration-bar customer'
                  style={{
                    width: `${(customerVal / barMax) * 100}%`,
                    backgroundColor: colorMap[levels]
                  }}
                />
                <div
                  className='penetration-bar remainder'
                  style={{
                    width: `${(remainderVal / barMax) * 100}%`,
                    backgroundColor: colorMap[levels]
                  }}
                />
              </div>
            </InteractiveDiv>
          );
        })}
      </ChartContainer>
    </div>
  );
};

interface PenetrationPieProps {
  data: KnowYourCustomerData[];
  fieldPrettyName: string;
  setSelectedValue: Dispatch<SetStateAction<string | undefined>>;
  chartContainerRightActions: ReactElement;
  decimalPrecision: number;
}

const PenetrationPie = ({
  data,
  fieldPrettyName,
  setSelectedValue,
  chartContainerRightActions,
  decimalPrecision
}: PenetrationPieProps) => {
  const [hoveredValue, setHoveredValue] = useState<string | undefined>();
  const sizeRef = useRef<HTMLDivElement>(null);
  const pieSize = useContainerSize(sizeRef);
  const colorMap = useMemo(() => buildColorMap(data), [data]);

  const innerPieFormattedData = useMemo(
    () =>
      data.map(({ levels, tuPopulation }) => ({
        name: levels,
        value: Math.round(tuPopulation)
      })),
    [data]
  );

  const outerPieFormattedData = useMemo(() => {
    const formattedData: ChartDataPoint[] = [];

    data.forEach(({ levels, clientPopulation, tuPopulation }) => {
      formattedData.push({
        name: levels,
        value: Math.round(clientPopulation)
      });
      formattedData.push({
        name: `non-${levels}`,
        value: Math.round(tuPopulation - clientPopulation)
      });
    });

    return formattedData;
  }, [data]);

  const legendFormattedData = useMemo(
    () =>
      data.map(({ levels, clientPopulation, tuPopulation }) => ({
        name: levels,
        val1: formatNumber(clientPopulation, 0),
        val2: formatNumber(tuPopulation, 0),
        computedValue: formatPercent(clientPopulation / tuPopulation, decimalPrecision),
        color: colorMap[levels]
      })),
    [colorMap, data, decimalPrecision]
  );

  return (
    <div className='customer-insights-pie-chart penetration-pie-chart'>
      <ChartContainer title={fieldPrettyName} rightActions={chartContainerRightActions}>
        <div ref={sizeRef} className='pie-sizer'>
          {pieSize && (
            <PieChart width={pieSize.width} height={pieSize.height}>
              <Legend
                content={
                  <CustomerInsightsLegend
                    data={legendFormattedData}
                    hoveredValue={hoveredValue}
                    setHoveredValue={setHoveredValue}
                    setSelectedValue={setSelectedValue}
                  />
                }
                layout='vertical'
                align='left'
                verticalAlign='top'
              />
              <Pie
                className='penetration-inner-pie'
                data={innerPieFormattedData}
                isAnimationActive={false}
                startAngle={90}
                endAngle={-270}
                cx='50%'
                cy='50%'
                innerRadius='20%'
                outerRadius='80%'
                dataKey='value'
                onMouseEnter={slice => {
                  setHoveredValue(slice?.name);
                }}
                onMouseLeave={() => {
                  setHoveredValue(undefined);
                }}
              >
                {innerPieFormattedData.map(({ name }) => (
                  <Cell
                    className={cn('pie-cell', {
                      'hovered-value': name === hoveredValue,
                      'unhovered-value': Boolean(hoveredValue) && name !== hoveredValue
                    })}
                    key={`cell-${name}`}
                    fill={colorMap[name]}
                    onClick={() => setSelectedValue(name)}
                    onMouseEnter={() => {
                      setHoveredValue(name);
                    }}
                    onMouseLeave={() => {
                      setHoveredValue(undefined);
                    }}
                  />
                ))}
              </Pie>
              <Pie
                className='penetration-outer-pie'
                data={outerPieFormattedData}
                isAnimationActive={false}
                startAngle={90}
                endAngle={-270}
                cx='50%'
                cy='50%'
                innerRadius='20%'
                outerRadius='90%'
                dataKey='value'
              >
                {outerPieFormattedData.map(({ name }) => (
                  <Cell
                    className={cn('pie-cell', {
                      'hovered-value': name === hoveredValue,
                      'unhovered-value': Boolean(hoveredValue) && name !== hoveredValue
                    })}
                    key={`cell-${name}`}
                    fill={colorMap[name] ?? 'transparent'}
                    onClick={() =>
                      setSelectedValue(name.startsWith('non-') ? name.substring(4) : name)
                    }
                    onMouseEnter={() => {
                      setHoveredValue(name.startsWith('non-') ? name.substring(4) : name);
                    }}
                    onMouseLeave={() => {
                      setHoveredValue(undefined);
                    }}
                  />
                ))}
              </Pie>
            </PieChart>
          )}
        </div>
      </ChartContainer>
    </div>
  );
};

interface SelectedPieProps {
  data: KnowYourCustomerData[];
  fieldName: CustomerInsightsFields;
  fieldPrettyName: string;
  selectedValue: string;
  setSelectedValue: Dispatch<SetStateAction<string | undefined>>;
  chartContainerRightActions: ReactElement;
  decimalPrecision: number;
}

const SelectedPie = ({
  data,
  fieldName,
  fieldPrettyName,
  selectedValue,
  setSelectedValue,
  chartContainerRightActions,
  decimalPrecision
}: SelectedPieProps) => {
  const pieRef = useRef<HTMLDivElement>(null);
  const sizeRef = useRef<HTMLDivElement>(null);
  const pieSize = useContainerSize(sizeRef);
  const colorMap = useMemo(() => buildColorMap(data), [data]);

  const innerPieFormattedData = useMemo(() => {
    const formattedData: ChartDataPoint[] = [];

    const selectedData = data.find(({ levels }) => levels === selectedValue);
    if (selectedData) {
      formattedData.push({
        name: selectedData.levels,
        value: Math.round(selectedData.tuPopulation)
      });
    }

    return formattedData;
  }, [data, selectedValue]);

  const outerPieFormattedData = useMemo(() => {
    const formattedData: ChartDataPoint[] = [];

    const selectedData = data.find(({ levels }) => levels === selectedValue);
    if (selectedData) {
      formattedData.push({
        name: selectedData.levels,
        value: Math.round(selectedData.clientPopulation)
      });
      formattedData.push({
        name: `non-${selectedData.levels}`,
        value: Math.round(selectedData.tuPopulation - selectedData.clientPopulation)
      });
    }

    return formattedData;
  }, [data, selectedValue]);

  const legendFormattedData = useMemo(
    () =>
      data.map(({ levels, clientPopulation, tuPopulation }) => {
        let color: string | undefined;
        if (CHART_TYPE_MAP[fieldName] === ChartType.PIE) {
          color = levels === selectedValue ? colorMap[levels] : '#DFE2E6';
        }
        return {
          name: levels,
          val1: formatNumber(clientPopulation, 0),
          val2: formatNumber(tuPopulation, 0),
          computedValue: formatPercent(clientPopulation / tuPopulation, decimalPrecision),
          color
        };
      }),
    [colorMap, data, decimalPrecision, fieldName, selectedValue]
  );

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!pieRef.current) {
        return;
      }

      const legend = pieRef.current.querySelector('.recharts-legend-wrapper .legend');
      const innerPie = pieRef.current.querySelector('.recharts-pie.penetration-inner-pie');
      const outerPie = pieRef.current.querySelector('.recharts-pie.penetration-outer-pie');

      const target = event.target as Node | null;
      const withinLegend = legend?.contains(target);
      const withinInnerPie = innerPie?.contains(target);
      const withinOuterPie = outerPie?.contains(target);

      if (!(withinLegend || withinInnerPie || withinOuterPie)) {
        setSelectedValue(undefined);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [setSelectedValue]);

  return (
    <div className='customer-insights-pie-chart selected-penetration-pie-chart' ref={pieRef}>
      <ChartContainer title={fieldPrettyName} rightActions={chartContainerRightActions}>
        <div ref={sizeRef} className='pie-sizer'>
          {pieSize && (
            <PieChart width={pieSize.width} height={pieSize.height}>
              <Legend
                content={
                  <CustomerInsightsLegend
                    data={legendFormattedData}
                    selectedValue={selectedValue}
                    setSelectedValue={setSelectedValue}
                  />
                }
                layout='vertical'
                align='left'
                verticalAlign='top'
              />
              <Pie
                className='penetration-inner-pie'
                data={innerPieFormattedData}
                isAnimationActive={false}
                startAngle={90}
                endAngle={-270}
                cx='50%'
                cy='50%'
                innerRadius='20%'
                outerRadius='80%'
                dataKey='value'
                onClick={() => setSelectedValue(undefined)}
              >
                {innerPieFormattedData.map(({ name }) => (
                  <Cell className='pie-cell' key={`cell-${name}`} fill={colorMap[name]} />
                ))}
              </Pie>
              <Pie
                className='penetration-outer-pie'
                data={outerPieFormattedData}
                isAnimationActive={false}
                startAngle={90}
                endAngle={-270}
                cx='50%'
                cy='50%'
                innerRadius='20%'
                outerRadius='90%'
                dataKey='value'
                onClick={() => setSelectedValue(undefined)}
              >
                {outerPieFormattedData.map(({ name }) => (
                  <Cell
                    className='pie-cell'
                    key={`cell-${name}`}
                    fill={colorMap[name] ?? 'transparent'}
                  />
                ))}
              </Pie>
            </PieChart>
          )}
        </div>
      </ChartContainer>
    </div>
  );
};

interface PenetrationChartProps {
  fieldName: CustomerInsightsFields;
  data: KnowYourCustomerData[];
  decimalPrecision?: number;
}

export const PenetrationChart = ({
  fieldName,
  data,
  decimalPrecision = 1
}: PenetrationChartProps) => {
  const [selectedValue, setSelectedValue] = useState<string | undefined>();
  const fieldPrettyName = CUSTOMER_INSIGHTS_PRETTY_NAMES[fieldName];
  const roundedPrecision = Math.round(decimalPrecision);
  const chartType = CHART_TYPE_MAP[fieldName];

  if (selectedValue) {
    return (
      <SelectedPie
        data={data}
        fieldName={fieldName}
        fieldPrettyName={fieldPrettyName}
        selectedValue={selectedValue}
        setSelectedValue={setSelectedValue}
        chartContainerRightActions={
          <ChartContainerRightActions fieldName={fieldName} data={data} />
        }
        decimalPrecision={roundedPrecision}
      />
    );
  }

  if (chartType === ChartType.BAR) {
    return (
      <StackedBar
        data={data}
        fieldPrettyName={fieldPrettyName}
        setSelectedValue={setSelectedValue}
        chartContainerRightActions={
          <ChartContainerRightActions fieldName={fieldName} data={data} />
        }
        decimalPrecision={roundedPrecision}
      />
    );
  }

  if (chartType === ChartType.PIE) {
    return (
      <PenetrationPie
        data={data}
        fieldPrettyName={fieldPrettyName}
        setSelectedValue={setSelectedValue}
        chartContainerRightActions={
          <ChartContainerRightActions fieldName={fieldName} data={data} />
        }
        decimalPrecision={roundedPrecision}
      />
    );
  }

  return null;
};
